import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getBookingById } from '../api/booking';
import { useBookingContext } from '../context/BookingContext';
import useCustomerAuth from '../hooks/useCustomerAuth';
import { normalizeBookingDraft } from '../utils/bookingMapper';
import { formatDateRange, formatGuests, formatInr } from '../utils/bookingUtils';

const BookingSuccess = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const { clearBooking, setBookingId, setDraft } = useBookingContext();
  const { accessToken } = useCustomerAuth();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!bookingId) return;
    let active = true;
    setLoading(true);
    setError('');
    getBookingById(bookingId, accessToken)
      .then((res) => {
        if (!active) return;
        const normalized = normalizeBookingDraft(res);
        setBooking(normalized);
        if (normalized?.id) setBookingId(normalized.id);
        setDraft(normalized);
      })
      .catch((err) => {
        if (!active) return;
        setError(err.message || 'Failed to load booking.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [accessToken, bookingId, setBookingId, setDraft]);

  if (!bookingId) {
    return (
      <div className="min-h-screen bg-[#F6F2EC] px-4 py-10">
        <div className="mx-auto max-w-4xl rounded-3xl border border-dashed border-slate-200 bg-white p-10 text-center">
          <h2 className="text-2xl font-semibold text-slate-900">Booking not found</h2>
          <p className="mt-2 text-sm text-slate-600">We could not find a booking reference.</p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="mt-6 rounded-full bg-[#1F2A37] px-6 py-3 text-sm font-semibold text-white"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="p-4 text-slate-600">Loading confirmation...</div>;
  }

  if (error) {
    return <div className="min-h-screen bg-[#F6F2EC] px-4 py-10"><div className="mx-auto max-w-xl rounded-2xl border border-rose-200 bg-white p-6 text-center"><h1 className="text-xl font-semibold text-slate-900">Confirmation unavailable</h1><p className="mt-2 text-sm text-red-600">{error}</p><button type="button" onClick={() => navigate('/guest/bookings')} className="mt-5 rounded-full bg-[#1F2A37] px-6 py-3 text-sm font-semibold text-white">View my bookings</button></div></div>;
  }

  const verifiedStatus = String(booking?.paymentStatus || booking?.payment_status || booking?.status || '').toUpperCase();
  const isVerifiedBooking = ['PAID', 'CONFIRMED', 'PARTIALLY_PAID', 'PARTIAL_PAID'].includes(verifiedStatus);
  if (!isVerifiedBooking) {
    return <div className="min-h-screen bg-[#F6F2EC] px-4 py-10"><div className="mx-auto max-w-xl rounded-2xl border border-amber-200 bg-white p-6 text-center"><h1 className="text-xl font-semibold text-slate-900">Payment verification pending</h1><p className="mt-2 text-sm text-slate-600">We have not received a confirmed payment status for this booking yet.</p><button type="button" onClick={() => navigate(`/payment/${bookingId}`)} className="mt-5 rounded-full bg-[#1F2A37] px-6 py-3 text-sm font-semibold text-white">Check payment</button></div></div>;
  }

  const isPartialBooking = String(booking?.paymentOptionSelected || 'full').toLowerCase() === 'partial';

  return (
    <div className="relative min-h-screen bg-[#F6F2EC] px-4 py-10">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 right-0 h-72 w-72 rounded-full bg-[#E8D9C7] opacity-70 blur-3xl" />
        <div className="absolute left-0 top-20 h-64 w-64 rounded-full bg-[#F3E6D8] opacity-60 blur-3xl" />
      </div>
      <div className="relative mx-auto max-w-3xl space-y-6">
        <div className="rounded-2xl border border-[#E7DED4] bg-white p-8 text-center shadow-[0_18px_40px_rgba(15,23,42,0.1)]">
          <div className="text-sm uppercase tracking-[0.3em] text-emerald-600">
            {isPartialBooking ? 'Advance payment received' : 'Booking confirmed'}
          </div>
          <h1 className="mt-3 text-2xl font-semibold text-slate-900">
            {isPartialBooking ? 'Your booking is secured with a partial payment.' : 'Your stay is booked!'}
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Booking ID: <span className="font-semibold text-slate-900">#{booking?.id || bookingId}</span>
          </p>
        </div>
        <div className="rounded-2xl border border-[#E7DED4] bg-white p-6 shadow-[0_14px_34px_rgba(15,23,42,0.08)]">
          <h2 className="text-lg font-semibold text-slate-900">Stay details</h2>
          <div className="mt-3 space-y-2 text-sm text-slate-700">
            <div><span className="text-slate-500">Property:</span> {booking?.stayName || 'Homevilas stay'}</div>
            <div><span className="text-slate-500">Dates:</span> {formatDateRange(booking?.checkIn, booking?.checkOut)}</div>
            <div><span className="text-slate-500">Guests:</span> {formatGuests(booking?.guests)}</div>
            <div><span className="text-slate-500">Booking total:</span> {formatInr(booking?.pricing?.total || 0)}</div>
            <div><span className="text-slate-500">Paid now:</span> {formatInr((booking?.amountPaidNow ?? booking?.pricing?.total) || 0)}</div>
            {isPartialBooking ? (
              <>
                <div><span className="text-slate-500">Partial percentage:</span> {booking?.partialPaymentPercentage || 0}%</div>
                <div><span className="text-slate-500">Remaining balance:</span> {formatInr(booking?.remainingAmount || 0)}</div>
              </>
            ) : null}
          </div>
        </div>
        <div className="rounded-2xl border border-[#E7DED4] bg-white px-5 py-4 text-sm text-slate-600 shadow-[0_14px_34px_rgba(15,23,42,0.08)]">
          Need help? Call +918287703118 or email info@homevilas.com, meena@homevilas.com
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={() => {
              clearBooking();
              navigate('/');
            }}
            className="rounded-full bg-[#1F2A37] px-6 py-3 text-sm font-semibold text-white"
          >
            Go to Home
          </button>
          <button
            type="button"
            onClick={() => {
              navigate(`/invoice/${booking?.id || bookingId}`);
            }}
            className="rounded-full border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-700"
          >
            View invoice
          </button>
          <button
            type="button"
            onClick={() => {
              clearBooking();
              navigate('/guest/bookings');
            }}
            className="rounded-full border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-700"
          >
            My bookings
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingSuccess;
